import React, { useState } from 'react'

import { useToasts } from '@/hooks/useToasts'

import { useApolloClient } from '@apollo/client'
import { Tooltip } from '@chakra-ui/react'

import type { ColorScheme } from '@hivewatch/dark'

import { Button } from '@/components/ui'
import type { AssociateDeviceI, ConnectionI } from '@/features/floor-plan'
import {
  AssociateDeviceModal,
  DarkAssociateDeviceModal,
} from '@/features/floor-plan'
import {
  DarkTailgateGridModal,
  TailgateGridModal,
  TailgateGridProvider,
  useTailgateMixpanelEvents,
} from '@/features/tailgate-grid'
import {
  useDoorTailgateGridsEnabledQuery,
  useModifyTailgateConnMutation,
  useTailgateGridQuery,
  useUpdateTailgateEnabledMutation,
} from '@/graphql/generated/hooks'
import {
  DeviceType,
  UpdateTailgateEnabledInput,
} from '@/graphql/generated/schemas'
import { includeIfActive } from '@/utils/apollo'

interface TailgateGridForDeviceIProps {
  doorId: string
  doorName: string
  cameraId: string
  cameraName: string
  deviceType: DeviceType
  connectionId: string
  onSaveCallback?: () => void
  renderTrigger?: (onClick: () => void, isDisabled: boolean) => React.ReactNode
  colorScheme?: ColorScheme
  overlayZIndex?: number
}

export const EditTailgateGridButton = ({
  doorId,
  doorName,
  cameraId,
  cameraName,
  deviceType,
  connectionId,
  onSaveCallback,
  renderTrigger,
  colorScheme,
  overlayZIndex,
}: TailgateGridForDeviceIProps) => {
  const [isAssociateDeviceOpen, setIsAssociateDeviceOpen] = useState(false)
  const [isTailgateGridOpen, setIsTailgateGridOpen] = useState(false)
  const { showError } = useToasts()

  const { data: tailgateGridsData } = useDoorTailgateGridsEnabledQuery({
    fetchPolicy: 'network-only',
    skip: !isAssociateDeviceOpen,
    variables: { id: doorId },
  })

  const { data: gridData } = useTailgateGridQuery({
    fetchPolicy: 'network-only',
    skip: !isAssociateDeviceOpen && !isTailgateGridOpen,
    variables: { doorId, cameraId },
  })

  const isTailgateEnabled =
    tailgateGridsData?.device?.door?.tailgateGrids?.find(
      (g) => g?.camera?.id === cameraId
    )?.isTailgateEnabled ?? false

  const isTailgateGridConfigured = !!gridData?.tailgateGrid?.inBox

  const connection: ConnectionI = {
    id: connectionId,
    isConnected: true,
    isTailgateGridConfigured,
    isTailgateEnabled,
    deviceId: doorId,
    cameraId,
    deviceType,
    lineCoords: [],
  }

  const associateDeviceValues: AssociateDeviceI = {
    cameraId,
    cameraName,
    doorId,
    doorName,
    deviceType,
  }

  const { trackTailgateEvent } = useTailgateMixpanelEvents({
    facilityId: '',
    floorId: '',
    cameraId,
    doorId,
  })

  const [updateTailgateEnabled, { loading: isEnableTailgateLoading }] =
    useUpdateTailgateEnabledMutation({
      // AssociatedDevices drives the association-status tag in the device
      // drawer tab — without it the tag never reaches Tailgate Enabled.
      refetchQueries: ['DoorTailgateGridsEnabled', 'AssociatedDevices'],
    })

  const handleEnableTailgate = async (enabled: boolean) => {
    const input: UpdateTailgateEnabledInput = {
      cameraId,
      doorId,
      isTailgateEnabled: enabled,
    }
    try {
      await updateTailgateEnabled({ variables: { input } })
    } catch {
      showError('Error updating tailgate')
    }
  }

  // Disassociating changes the Associations column in the Devices table
  // (DeviceAssociationCounts) mounted behind the drawer or edit form.
  const client = useApolloClient()
  const [modifyTailgateConnection, { loading: isDisassociating }] =
    useModifyTailgateConnMutation({
      refetchQueries: () => [
        'DeviceAssocationOptions',
        ...includeIfActive(client, 'DeviceAssociationCounts'),
      ],
    })

  const handleDisassociate = async () => {
    try {
      await modifyTailgateConnection({
        variables: {
          input: {
            id: connectionId,
            cameraIdsToAdd: [],
            cameraIdsToRemove: [cameraId],
          },
        },
      })
      setIsAssociateDeviceOpen(false)
    } catch {
      showError('Error disassociating devices')
    }
  }

  const handleOpenGrid = () => {
    setIsAssociateDeviceOpen(false)
    setIsTailgateGridOpen(true)
  }
  const shouldDisableButton = !(
    deviceType === DeviceType.Door || deviceType === DeviceType.Camera
  )

  return (
    <>
      {renderTrigger ? (
        renderTrigger(() => setIsAssociateDeviceOpen(true), shouldDisableButton)
      ) : (
        <Tooltip
          hasArrow={true}
          isDisabled={!shouldDisableButton}
          label='Tailgate grids are only available for Doors and Cameras'
          placement='right'
          // shouldWrapChildren is required to help anchor Tooltip. See this comment for more context + visual: https://github.com/hivewatch/frontend-monorepo/pull/1724#discussion_r2913816990
          shouldWrapChildren
        >
          <Button
            isDisabled={shouldDisableButton}
            onClick={() => setIsAssociateDeviceOpen(true)}
            size='md'
            variant='secondary'
          >
            Edit Tailgating
          </Button>
        </Tooltip>
      )}
      {isAssociateDeviceOpen &&
        (colorScheme ? (
          <DarkAssociateDeviceModal
            associateDevice={associateDeviceValues}
            connection={connection}
            hideAssociateButton={true}
            isEnableTailgateLoading={isEnableTailgateLoading}
            isOpen={isAssociateDeviceOpen}
            loading={isDisassociating}
            onClose={() => setIsAssociateDeviceOpen(false)}
            onDisassociate={handleDisassociate}
            onEnableTailgate={handleEnableTailgate}
            onOpenGrid={handleOpenGrid}
            zIndex={overlayZIndex}
          />
        ) : (
          <AssociateDeviceModal
            associateDevice={associateDeviceValues}
            connection={connection}
            hideAssociateButton={true}
            isEnableTailgateLoading={isEnableTailgateLoading}
            isOpen={isAssociateDeviceOpen}
            loading={isDisassociating}
            onClose={() => setIsAssociateDeviceOpen(false)}
            onDisassociate={handleDisassociate}
            onEnableTailgate={handleEnableTailgate}
            onOpenGrid={handleOpenGrid}
          />
        ))}
      <TailgateGridProvider
        associateDeviceValues={associateDeviceValues}
        isGridConfigured={isTailgateGridConfigured}
        isTailgateGridOpen={isTailgateGridOpen}
        // A saved grid closes the editor (useUpdateTailgateGrid calls onClose
        // first); returning to the association modal keeps the flow alive so
        // the operator can flip Enable Tailgate — its grid queries are
        // network-only and re-run on reopen, so the toggle unlocks.
        onSaveCallback={() => {
          setIsAssociateDeviceOpen(true)
          onSaveCallback?.()
        }}
        onTailgateGridClose={() => setIsTailgateGridOpen(false)}
        shouldRenderDummyPlayer={isTailgateGridOpen}
        trackTailgateEvent={trackTailgateEvent}
      >
        {colorScheme ? (
          <DarkTailgateGridModal zIndex={overlayZIndex} />
        ) : (
          <TailgateGridModal />
        )}
      </TailgateGridProvider>
    </>
  )
}
